import { ChangeEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { DEVICE_MODEL_ACTIONS, DialogProps } from "components/Actions/types";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import ParamsContext from "context/params/paramsContext";
import {
    DROPDOWN_SIZES,
    ENDPOINTS,
    PLATFORMS,
    TABLE_NAMES,
    useApi
} from "shared";

import DialogActions from "../../components/DialogActions";
import { Data } from "./types";

const CreateModel = ({ isOpen, close }: DialogProps) => {
    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();

    const [isLoading, setIsLoading] = useState(false);

    const [data, setData] = useState<Data>({
        name: "",
        platform: null,
        hasTctWebSupport: false
    });

    const { name, platform, hasTctWebSupport } = data;

    const platforms = Object.values(PLATFORMS).filter(
        platform => platform !== PLATFORMS.Unknown
    );

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const payload = {
                name,
                platform,
                web_editor_support: hasTctWebSupport
            };

            const response = await postData(ENDPOINTS.DeviceModels, payload);

            handleCloseDialog();

            setReloadItems([
                TABLE_NAMES.DeviceModels,
                TABLE_NAMES.DeviceModelTaskCompatibilities
            ]);

            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleCloseDialog = () => close(DEVICE_MODEL_ACTIONS.CreateModel);

    const changeName = (e: ChangeEvent<HTMLInputElement>) =>
        setData(prev => ({ ...prev, name: e.target.value }));

    const changePlatform = (platform: PLATFORMS | null) =>
        setData(prev => ({ ...prev, platform }));

    const changeHasTctWebSupport = (value: boolean) =>
        setData(prev => ({ ...prev, hasTctWebSupport: value }));

    return (
        <Dialog
            title={t("Dialog##create model")}
            description={t("Dialog##create model description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setData({
                            name: "",
                            platform: null,
                            hasTctWebSupport: false
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    isReadyToConfirm={name && platform}
                    confirmationButtonText={t("Button##create")}
                />
            }
            extendToMaxWidth
        >
            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                readOnly={isLoading}
                onChange={changeName}
                labelLeft={t("Dialog##name")}
                placeholder={t("Dialog##enter name")}
                customStyle={{ marginBottom: "16px" }}
            />

            <Autocomplete
                fullWidth
                isRequired
                labelLeft={t("Table##platform")}
                size={DROPDOWN_SIZES.Medium}
                options={platforms}
                onChange={(_, val) => changePlatform(val)}
                readOnly={isLoading}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
            />

            <Checkbox
                label={t("Table##tct web support")}
                customStyle={{ marginTop: "16px" }}
                onChange={(_, isChecked) => changeHasTctWebSupport(isChecked)}
                disabled={isLoading}
            />
        </Dialog>
    );
};

export default CreateModel;
